import { Observable } from "rxjs";
import { Epic } from "redux-observable";
import { List, fromJS } from "immutable";
import diff from "immutablediff";
import { formatMessage } from "lib/globalize-util";
import siteConfig from "site-config";
import { ajax } from "lib/observable";
import { IStore } from "reducers";
import { getAllocationDimensionById, getAllocationDimensions } from "reducers/selectors/collection";
import { COLLECTIONS, MUTATE_OPERATIONS_SENT, MUTATE_UPDATE_SNAPSHOT_FROM_COMMITTED } from "constants/action-types";
import { ALLOCATION_DIMENSION_ID_PREFIX, ALLOCATION_DIMENSIONS_NAMESPACE } from "constants/product-collection";
import { BulkMutateAllocationDimensionValuesAction } from "actions/collections";
import {
    buildMutateOperation,
    AllocationDimensionOperationKey,
    IUpdateAllocationDimensionValue,
} from "helpers/allocation-dimension";
import { findDimensionValue } from "helpers/product-collection";
import { MutableType, IOperation, OperationType, bulkMixedSetOperation } from "actions/mutate";
import { add as snackbarAdd } from "actions/snackbars";
import { getAndIncrementRequestId } from "./helpers";

const allocationDimensionValueOperationMap = {
    [OperationType.Insert]: AllocationDimensionOperationKey.InsertDimensionValue,
    [OperationType.Set]: AllocationDimensionOperationKey.SetDimensionValue,
    [OperationType.Move]: AllocationDimensionOperationKey.MoveDimensionValue,
};
const normalizeToAllocationDimensionValueOperation = (operation: IOperation) => {
    if (operation.childKey === "dimension_values") {
        // Mutating allocation dimension's `dimension_values`
        const mappedOperationType = allocationDimensionValueOperationMap[operation.operation];

        let operationValue;
        switch (mappedOperationType) {
            case AllocationDimensionOperationKey.InsertDimensionValue:
            case AllocationDimensionOperationKey.SetDimensionValue:
                operationValue = {
                    index: operation.index,
                    value: operation.value.get("struct_value").toJS(),
                };
                break;
            case AllocationDimensionOperationKey.MoveDimensionValue:
                operationValue = {
                    index: operation.index,
                    destination_index: operation.destination_index,
                };
                break;
            default:
                return undefined;
        }

        return buildMutateOperation(mappedOperationType, operationValue);
    } else if (operation.childKey === "field_map") {
        // Mutating allocation dimension's `field_map`, these fields include `name`, `active` and `attributes`.

        switch (operation.key) {
            case "attributes":
                return buildMutateOperation(AllocationDimensionOperationKey.SetAttributes, {
                    keys: operation.value.getIn(["struct_value", "keys"]).toJS(),
                });
            default:
                return undefined;
        }
    }
};

// TODO: BACKUP. Added logic to debounce bulk allocation dimension values mutations.
// REMOVE IF THERE IS NO MORE ISSUE FOUND IN QA.
// export const mutateCollectionAllocationDimensionValueEpic: Epic<MutateAllocationDimensionValueAction, IStore> = (action$, store$) => {
//     let buffers = {};
//     return action$
//         .ofType(COLLECTIONS.COLLECTION.ALLOCATION_DIMENSION.VALUE.MUTATE.START)
//         .map((action) => {
//             const { allocationDimensionId, item } = action.payload;
//             if (!buffers[allocationDimensionId]) {
//                 buffers[allocationDimensionId] = [];
//             }

//             buffers[allocationDimensionId].push(item);
//         })
//         .debounce(() => Observable.interval(50))
//         .flatMap(() => {
//             const actions = [];
//             const state = store$.getState();
//             for (const allocationDimensionId of Object.keys(buffers)) {
//                 const allocationDimension = getAllocationDimensionById(state, allocationDimensionId);
//                 if (!allocationDimension) {
//                     continue;
//                 }

//                 const items = buffers[allocationDimensionId];
//                 const operations = [];

//                 items.forEach((item) => {
//                     const index = allocationDimension?.dimension_values.findIndex((currentItem) => currentItem.id === item.id);
//                     const mutatedItem = allocationDimension?.dimension_values[index];

//                     if (!mutatedItem) {
//                         const immutableItem = fromJS({
//                             struct_value: item,
//                         });

//                         operations.push({
//                             type: OperationType.Insert,
//                             index: allocationDimension?.dimension_values.length,
//                             childKey: "dimension_values",
//                             value: immutableItem,
//                         });
//                     } else {
//                         // Wrap with "struct_value" to match how it is stored in mutate.snapshots
//                         const immutableItem = fromJS({
//                             struct_value: item,
//                         });
//                         const deltas = diff(
//                             fromJS({
//                                 struct_value: mutatedItem,
//                             }),
//                             immutableItem
//                         );

//                         operations.push({
//                             type: OperationType.Set,
//                             index,
//                             childKey: "dimension_values",
//                             value: immutableItem,
//                             deltas,
//                         });
//                     }
//                 });

//                 actions.push(bulkMixedSetOperation(allocationDimensionId, MutableType.List, operations));
//             }
//             buffers = {};

//             return actions;
//         });
// };

export const bulkMutateCollectionAllocationDimensionValueEpic: Epic<
    BulkMutateAllocationDimensionValuesAction,
    IStore
> = (action$, store$) =>
    action$.ofType(COLLECTIONS.COLLECTION.ALLOCATION_DIMENSION.VALUE.BULK_MUTATE.START).concatMap((action) => {
        const { allocationDimensionId, items } = action.payload;
        const actions = [];
        const state = store$.getState();

        const allocationDimension = getAllocationDimensionById(state, allocationDimensionId);
        if (!allocationDimension) {
            return actions;
        }

        const operations = [];

        items.forEach((item) => {
            const index = allocationDimension?.dimension_values.findIndex(
                (currentItem) => currentItem.id === (item as IUpdateAllocationDimensionValue).id
            );
            const mutatedItem = allocationDimension?.dimension_values[index];

            if (!mutatedItem) {
                const immutableItem = fromJS({
                    struct_value: item,
                });

                operations.push({
                    type: OperationType.Insert,
                    index: allocationDimension?.dimension_values.length,
                    childKey: "dimension_values",
                    value: immutableItem,
                });
            } else {
                // Wrap with "struct_value" to match how it is stored in mutate.snapshots
                const immutableItem = fromJS({
                    struct_value: item,
                });
                const deltas = diff(
                    fromJS({
                        struct_value: mutatedItem,
                    }),
                    immutableItem
                );

                operations.push({
                    type: OperationType.Set,
                    index,
                    childKey: "dimension_values",
                    value: immutableItem,
                    deltas,
                });
            }
        });

        if (operations.length > 0) {
            actions.push(bulkMixedSetOperation(allocationDimensionId, MutableType.List, operations));
        }

        return actions;
    });

export const syncCollectionAllocationDimensionEpic = (action$, store$) =>
    action$
        .ofType(COLLECTIONS.COLLECTION.ALLOCATION_DIMENSION.SYNC.START)
        // Dont proceed if there is something sending or there is nothing to send
        .filter(({ payload: { objectId } }) => {
            const { mutate } = store$.getState();
            const { sentMutations, uncommittedMutations } = mutate;
            const sentMutation = sentMutations.get(objectId);
            const uncommittedMutation = uncommittedMutations.get(objectId) || List();

            return !sentMutation && !!uncommittedMutation.size;
        })
        .map(({ payload: { objectId } }: { payload: { objectId: string } }) => {
            const { collection, mutate } = store$.getState();
            const { committedHEADs, uncommittedMutations } = mutate;
            const { sessionId, merchantId } = collection;

            const committedHEAD = committedHEADs.get(objectId);

            const operations = [];
            uncommittedMutations.get(objectId).forEach((mutation) => {
                mutation.operations.forEach((operation) => {
                    const normalizedOperation = normalizeToAllocationDimensionValueOperation(operation);
                    if (normalizedOperation) {
                        operations.push(normalizedOperation);
                    }
                });
            });
            const requestId = getAndIncrementRequestId(objectId);
            const revision = parseInt(committedHEAD.revision, 10) + 1;
            const dataToSave = {
                req_id: requestId,
                operations,
                revision,
            };

            store$.dispatch({
                type: MUTATE_OPERATIONS_SENT,
                payload: {
                    requestId,
                    objectId,
                    revision,
                    objectType: committedHEAD.object_type,
                },
            });

            // Allocation dimension id is prepended by "allocation_dimension_"
            const allocationDimensionId = objectId.replace(ALLOCATION_DIMENSION_ID_PREFIX, "");

            return {
                dataToSave,
                sessionId,
                requestId,
                collectionId: committedHEAD.document_id,
                allocationDimensionId,
                merchantId,
            };
        })
        // Make it a queue, wait for response before continuing
        .concatMap(({ dataToSave, collectionId, requestId, sessionId, allocationDimensionId, merchantId }) => {
            const url = `/v1/collections/${collectionId}/allocation_dimensions/${allocationDimensionId}?sid=${sessionId}&req_id=${requestId}`;
            return ajax({
                body: { ...dataToSave, merchant_id: merchantId },
                method: "POST",
                url: siteConfig.goAPI + url,
            })
                .map(() => ({
                    payload: { collectionId },
                    type: COLLECTIONS.COLLECTION.ALLOCATION_DIMENSION.SYNC.COMPLETED,
                }))
                .catch(() => {
                    return Observable.merge(
                        Observable.of(
                            snackbarAdd({
                                message: formatMessage("There was an error while saving. Please try again."),
                            })
                        )
                    );
                });
        });

// On snapshot updated from committed, clear memoization cache to prevent memory leak
export const clearAllocationDimensionsMemoize = (action$) =>
    action$
        .ofType(MUTATE_UPDATE_SNAPSHOT_FROM_COMMITTED)
        .filter(({ payload }) => {
            const { committedHEAD } = payload;
            const { object_type } = committedHEAD;

            return object_type === ALLOCATION_DIMENSIONS_NAMESPACE;
        })
        .switchMap(() => {
            getAllocationDimensions.cache.clear();
            findDimensionValue.cache.clear();
            return Observable.empty();
        });
